import React, { useState, useEffect } from 'react';
import { Button } from '../../../ui-components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Input } from '../../../ui-components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui-components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui-components/ui/table';
import { Badge } from '../../../ui-components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui-components/ui/dialog';
import { Label } from '../../../ui-components/ui/label';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Download, MapPin } from 'lucide-react';
import useApiRequest from '../../../lib/hooks/use-request';
import { PageTitle, PageTitleContainer } from '../../../ui-components/utils/menu-items/page-title';

interface PLZRoutingRule {
  _id?: string;
  plzPrefix: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  isActive: boolean;
  priority: number;
  createdAt?: string;
  updatedAt?: string;
}

interface PLZStats {
  totalRules: number;
  activeRules: number;
  unassignedRules: number;
  coveredPLZCount: number;
}

const PLZRoutingPage = () => {
  const [rules, setRules] = useState<PLZRoutingRule[]>([]);
  const [stats, setStats] = useState<PLZStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<PLZRoutingRule | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    plzPrefix: '',
    assignedToUserId: '',
    priority: 1
  });

  const { apiRequest } = useApiRequest();

  // Dummy users for now
  const dummyUsers = [
    { _id: '1', name: 'Andreas Osterrieder' },
    { _id: '2', name: 'Andreas Poschmann' },
    { _id: '3', name: 'Christian Kaiser' },
    { _id: '4', name: 'Clemens Karge' },
    { _id: '5', name: 'Dirk Bienert' },
    { _id: '6', name: 'Felix Endres' },
    { _id: '7', name: 'Freddy Blum' },
    { _id: '17', name: 'Andi Hysa' },
  ];

  useEffect(() => {
    fetchRules();
    fetchStats();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    
    // Try to get from localStorage first
    const storedRules = localStorage.getItem('plz_routing_rules');
    if (storedRules) {
      setRules(JSON.parse(storedRules));
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiRequest<PLZRoutingRule[]>('settings/plz-routing', 'GET');
      if (response.data) {
        setRules(response.data);
        localStorage.setItem('plz_routing_rules', JSON.stringify(response.data));
      }
    } catch (error) {
      // Use dummy data if API fails
      const dummyRules: PLZRoutingRule[] = [
        { _id: '1', plzPrefix: '80', assignedToUserId: '7', assignedToUserName: 'Freddy Blum', isActive: true, priority: 1 },
        { _id: '2', plzPrefix: '81', assignedToUserId: '1', assignedToUserName: 'Andreas Osterrieder', isActive: true, priority: 1 },
        { _id: '3', plzPrefix: '82', assignedToUserId: '', assignedToUserName: '', isActive: true, priority: 1 },
        { _id: '4', plzPrefix: '83', assignedToUserId: '3', assignedToUserName: 'Christian Kaiser', isActive: true, priority: 1 },
        { _id: '5', plzPrefix: '84', assignedToUserId: '', assignedToUserName: '', isActive: true, priority: 1 },
        { _id: '6', plzPrefix: '85', assignedToUserId: '17', assignedToUserName: 'Andi Hysa', isActive: true, priority: 1 },
      ];
      setRules(dummyRules);
      localStorage.setItem('plz_routing_rules', JSON.stringify(dummyRules));
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await apiRequest<PLZStats>('settings/plz-routing/stats', 'GET');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      // Calculate stats from localStorage
      const storedRules = JSON.parse(localStorage.getItem('plz_routing_rules') || '[]');
      setStats({
        totalRules: storedRules.length,
        activeRules: storedRules.filter((r: PLZRoutingRule) => r.isActive).length,
        unassignedRules: storedRules.filter((r: PLZRoutingRule) => !r.assignedToUserId).length,
        coveredPLZCount: storedRules.length * 100
      });
    }
  };

  const handleSave = async () => {
    if (!formData.plzPrefix) {
      toast.error('Bitte PLZ-Präfix eingeben');
      return;
    }

    const dataToSave = {
      ...formData,
      assignedToUserId: formData.assignedToUserId === 'unassigned' ? '' : formData.assignedToUserId,
      isActive: true
    };

    try {
      // Get current rules from localStorage
      const currentRules = JSON.parse(localStorage.getItem('plz_routing_rules') || '[]');
      
      if (editingRule) {
        // Update existing rule
        const updatedRules = currentRules.map((rule: PLZRoutingRule) => 
          rule._id === editingRule._id 
            ? { ...rule, ...dataToSave, assignedToUserName: dataToSave.assignedToUserId ? dummyUsers.find(u => u._id === dataToSave.assignedToUserId)?.name || '' : '' }
            : rule
        );
        localStorage.setItem('plz_routing_rules', JSON.stringify(updatedRules));
        setRules(updatedRules);
        
        // Try API call too
        await apiRequest(`settings/plz-routing/${editingRule._id}`, 'PUT', {
          body: dataToSave
        }).catch(() => {});
        
        toast.success('PLZ-Regel aktualisiert');
      } else {
        // Create new rule
        const newRule: PLZRoutingRule = {
          _id: `local_${Date.now()}`,
          ...dataToSave,
          assignedToUserName: dataToSave.assignedToUserId ? dummyUsers.find(u => u._id === dataToSave.assignedToUserId)?.name || '' : '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const updatedRules = [...currentRules, newRule];
        localStorage.setItem('plz_routing_rules', JSON.stringify(updatedRules));
        setRules(updatedRules);
        
        // Try API call too
        await apiRequest('settings/plz-routing', 'POST', {
          body: dataToSave
        }).catch(() => {});
        
        toast.success('PLZ-Regel erstellt');
      }
      
      setShowAddDialog(false);
      setEditingRule(null);
      setFormData({ plzPrefix: '', assignedToUserId: '', priority: 1 });
      
      // Update stats
      const updatedRules = JSON.parse(localStorage.getItem('plz_routing_rules') || '[]');
      setStats({
        totalRules: updatedRules.length,
        activeRules: updatedRules.filter((r: PLZRoutingRule) => r.isActive).length,
        unassignedRules: updatedRules.filter((r: PLZRoutingRule) => !r.assignedToUserId).length,
        coveredPLZCount: updatedRules.length * 100
      });
    } catch (error) {
      toast.error('Fehler beim Speichern der PLZ-Regel');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese PLZ-Regel löschen möchten?')) {
      return;
    }

    try {
      // Delete from localStorage
      const currentRules = JSON.parse(localStorage.getItem('plz_routing_rules') || '[]');
      const updatedRules = currentRules.filter((rule: PLZRoutingRule) => rule._id !== id);
      localStorage.setItem('plz_routing_rules', JSON.stringify(updatedRules));
      setRules(updatedRules);
      
      // Update stats
      setStats({
        totalRules: updatedRules.length,
        activeRules: updatedRules.filter((r: PLZRoutingRule) => r.isActive).length,
        unassignedRules: updatedRules.filter((r: PLZRoutingRule) => !r.assignedToUserId).length,
        coveredPLZCount: updatedRules.length * 100
      });
      
      // Try API call too
      await apiRequest(`settings/plz-routing/${id}`, 'DELETE').catch(() => {});
      
      toast.success('PLZ-Regel gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen der PLZ-Regel');
    }
  };

  const handleEdit = (rule: PLZRoutingRule) => {
    setEditingRule(rule);
    setFormData({
      plzPrefix: rule.plzPrefix,
      assignedToUserId: rule.assignedToUserId || 'unassigned',
      priority: rule.priority
    });
    setShowAddDialog(true);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await apiRequest('settings/plz-routing/import/csv', 'POST', {
        body: formData
      });
      toast.success('PLZ-Regeln importiert');
      fetchRules();
      fetchStats();
    } catch (error) {
      toast.error('Fehler beim Importieren der PLZ-Regeln');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiRequest<PLZRoutingRule[]>('settings/plz-routing/export', 'GET');
      if (response.data) {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        
        const exportFileDefaultName = `plz-routing-export-${new Date().toISOString().split('T')[0]}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
    } catch (error) {
      toast.error('Fehler beim Exportieren der PLZ-Regeln');
    }
  };

  const filteredRules = rules.filter(rule => 
    rule.plzPrefix.includes(searchTerm) ||
    rule.assignedToUserName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <PageTitleContainer>
        <PageTitle>
          <div>
            <h1 className="text-2xl font-bold">PLZ-Routing Übersicht</h1>
            <p className="text-gray-600 mt-1">Verwalten Sie die Zuweisung von Postleitzahlbereichen an Vertriebsmitarbeiter</p>
          </div>
        </PageTitle>
      </PageTitleContainer>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-blue-900 mb-1">So funktioniert PLZ-Routing</h3>
              <p className="text-sm text-blue-700">
                Definieren Sie PLZ-Bereiche und weisen Sie diese Ihren Vertriebsmitarbeitern zu. 
                Wenn neue Leads mit einer Postleitzahl angelegt werden, werden sie automatisch dem 
                zuständigen Mitarbeiter zugewiesen. Bei überlappenden Bereichen gilt die höhere Priorität.
              </p>
              <div className="mt-2 flex items-center gap-4 text-xs text-blue-600">
                <span className="flex items-center gap-1">
                  <Badge variant="outline" className="bg-white">Beispiel:</Badge>
                  PLZ 80335 → Präfix "80" → Max Mustermann
                </span>
                <span className="flex items-center gap-1">
                  <Badge variant="outline" className="bg-white">Tipp:</Badge>
                  Nutzen Sie 2-5 stellige Präfixe für feinere Kontrolle
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gesamt PLZ-Regeln</p>
                  <p className="text-2xl font-bold">{stats.totalRules}</p>
                </div>
                <MapPin className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aktive Regeln</p>
                  <p className="text-2xl font-bold text-green-600">{stats.activeRules}</p>
                </div>
                <Badge variant="default" className="bg-green-100 text-green-800">Aktiv</Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Nicht zugewiesen</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.unassignedRules}</p>
                </div>
                <Badge variant="default" className="bg-orange-100 text-orange-800">Offen</Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Abgedeckte PLZ</p>
                  <p className="text-2xl font-bold">{stats.coveredPLZCount}</p>
                </div>
                <span className="text-2xl">📍</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions Bar */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4 w-full md:w-auto">
              <Input
                placeholder="PLZ oder Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
                id="import-file"
              />
              <label htmlFor="import-file">
                <Button variant="outline" asChild>
                  <span className="cursor-pointer flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Import CSV
                  </span>
                </Button>
              </label>
              
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
              
              <Button onClick={() => {
                setEditingRule(null);
                setFormData({ plzPrefix: '', assignedToUserId: '', priority: 1 });
                setShowAddDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Neue PLZ-Regel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card>
        <CardHeader>
          <CardTitle>PLZ-Routing Regeln</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PLZ-Präfix</TableHead>
                <TableHead>Zugewiesen an</TableHead>
                <TableHead>Priorität</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    Lade PLZ-Regeln...
                  </TableCell>
                </TableRow>
              ) : filteredRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    Keine PLZ-Regeln gefunden
                  </TableCell>
                </TableRow>
              ) : (
                filteredRules.map((rule) => (
                  <TableRow key={rule._id}>
                    <TableCell className="font-medium">{rule.plzPrefix}xxx</TableCell>
                    <TableCell>
                      {rule.assignedToUserName ? (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                            {rule.assignedToUserName.charAt(0)}
                          </div>
                          {rule.assignedToUserName}
                        </div>
                      ) : (
                        <span className="text-orange-600 font-medium">Nicht zugewiesen</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{rule.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {rule.isActive ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(rule)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => rule._id && handleDelete(rule._id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'PLZ-Regel bearbeiten' : 'Neue PLZ-Regel erstellen'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>PLZ-Präfix</Label>
              <Input
                value={formData.plzPrefix}
                onChange={(e) => setFormData({ ...formData, plzPrefix: e.target.value })}
                placeholder="z.B. 80, 81, 823"
                maxLength={5}
              />
              <p className="text-sm text-gray-500 mt-1">
                Geben Sie die ersten Ziffern der PLZ ein (1-5 Stellen)
              </p>
            </div>
            
            <div>
              <Label>Zugewiesen an</Label>
              <Select
                value={formData.assignedToUserId}
                onValueChange={(value) => setFormData({ ...formData, assignedToUserId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                  {dummyUsers.map((user) => (
                    <SelectItem key={user._id} value={user._id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Priorität</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                min="1"
                max="10"
              />
              <p className="text-sm text-gray-500 mt-1">
                Höhere Priorität wird bei Überschneidungen bevorzugt
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave}>
              {editingRule ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PLZRoutingPage;
